import { Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild } from '@angular/core';

@Component({
  selector: 'app-file-uploader',
  standalone: false,
  templateUrl: './file-uploader.component.html',
  styleUrl: './file-uploader.component.scss'
})
export class FileUploaderComponent {
  @Input() accept: string = '';
  @Input() multiple: boolean = true;
  @Input() maxFiles: number | null = null; // null means no limit
  @Input() maxFileSizeMB: number = 5;
  /** Shorter dropzone for use inside a busy window (e.g. a card in a modal). Default keeps the full-size zone. */
  @Input() compact: boolean = false;
  @Output() onChanged = new EventEmitter<File[]>();

  @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;

  files: File[] = [];
  previews: string[] = [];
  error: string = '';

  get formattedAccept(): string {
    if (!this.accept) return '';
    return this.accept
      .split(',')
      .map(s => s.trim().replace(/^\./, '').toUpperCase())
      .join(', ');
  }

  onFileDropped(event: DragEvent) {
    event.preventDefault();
    if (event.dataTransfer?.files) {
      this.handleFiles(event.dataTransfer.files);
    }
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.handleFiles(input.files);
    }
  }

  handleFiles(fileList: FileList) {
    const newFiles = Array.from(fileList);

    for (const file of newFiles) {
      if (!this.multiple && this.files.length >= 1) {
        this.error = `Only one file is allowed.`;
        return;
      }

      this.setFileExceedsError()

      const sizeMB = file.size / (1024 * 1024);
      if (sizeMB > this.maxFileSizeMB) {
        this.error = `File "${file.name}" exceeds ${this.maxFileSizeMB} MB limit.`;
        continue;
      }

      this.files.push(file);
      this.generatePreview(file);
    }

    this.onChanged.emit(this.files);
  }

  // Set error if file exceeds max length
  setFileExceedsError() {
    // If maxFiles is null, no limit 
    if (!!this.maxFiles && this.files.length > this.maxFiles) {
      this.error = `Maximum ${this.maxFiles} files allowed.`;
      return;
    } 

    this.error = '';
  }

  generatePreview(file: File) {
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = () => {
        this.previews.push(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      this.previews.push('');
    }
  }

  removeFile(index: number) {
    this.files.splice(index, 1);
    this.previews.splice(index, 1);
    this.onChanged.emit(this.files);
    this.setFileExceedsError()
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
  }

  // While an uploader is on screen, a file dropped just outside the dropzone must not fall through
  // to the browser, which would open it and navigate away - losing a half-filled form. Only real
  // file drags are swallowed, so dragging text between fields still works.
  @HostListener('window:dragover', ['$event'])
  @HostListener('window:drop', ['$event'])
  onWindowFileDrag(event: DragEvent) {
    if (event.defaultPrevented) return;
    if (Array.from(event.dataTransfer?.types ?? []).includes('Files')) {
      event.preventDefault();
      if (event.dataTransfer && event.type === 'dragover') event.dataTransfer.dropEffect = 'none';
    }
  }

  openFileSelector() {
    this.fileInputRef.nativeElement.click();
  }

  clearFiles() {
    this.files = [];
    // Previews are index-aligned with files, so they must reset together; and the native
    // input keeps its value, which stops the same file being picked again after an upload.
    this.previews = [];
    this.error = '';
    if (this.fileInputRef?.nativeElement) {
      this.fileInputRef.nativeElement.value = '';
    }
  }
}

